/**
 * Tri des pièces PDF d'un dossier — Sprint 2 §1.3 + ADR-039.
 *
 * Deux modes :
 *  - ``sortPieces(pieces)`` (legacy) : tri par leading number / date_pv /
 *    uploaded_at / filename.
 *  - ``sortPiecesByInventory(pieces, entries, preference)`` : utilise
 *    l'ordre canonique de l'inventaire (carton/sous_farde/piece) pour les
 *    pièces liées à un ``InventaireEntry``, et applique le tri legacy
 *    pour les pièces non-liées. ``preference`` filtre les entries par
 *    source (``'auto'`` = toutes, ``'manual'``, ``'ai_parsed'``).
 *
 * Le sort est stable et insensible aux variantes de séparateurs (``-``, ``_``,
 * ``.``, espaces) avant le numéro.
 */
import type { InventaireEntry, PdfMetadata } from './types';

export type InventorySourcePreference = 'auto' | 'manual' | 'ai_parsed';

const LEADING_NUM_RE = /^[\s\-_.()]*(\d{1,5})\b/;

function leadingNumber(filename: string): number | null {
  const m = LEADING_NUM_RE.exec(filename);
  if (!m) return null;
  const n = parseInt(m[1]!, 10);
  return Number.isFinite(n) ? n : null;
}

function dateKey(d: string | null | undefined): number {
  if (!d) return Number.POSITIVE_INFINITY;
  const t = Date.parse(d);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

export function comparePieces(a: PdfMetadata, b: PdfMetadata): number {
  const na = leadingNumber(a.filename_original);
  const nb = leadingNumber(b.filename_original);
  if (na !== null && nb !== null && na !== nb) return na - nb;
  if (na !== null && nb === null) return -1;
  if (na === null && nb !== null) return 1;

  const da = dateKey(a.date_pv);
  const db = dateKey(b.date_pv);
  if (da !== db) return da - db;

  const ua = dateKey(a.uploaded_at);
  const ub = dateKey(b.uploaded_at);
  if (ua !== ub) return ua - ub;

  return a.filename_original.localeCompare(b.filename_original, 'fr', {
    sensitivity: 'base',
    numeric: true,
  });
}

export function sortPieces<T extends PdfMetadata>(pieces: readonly T[]): T[] {
  return [...pieces].sort(comparePieces);
}

/**
 * Clé de tri canonique d'un inventaire : (carton, sous_farde, piece) avec
 * tri naturel (« 2 » avant « 10 ») et nulls relégués en fin.
 */
function inventaireKey(e: InventaireEntry): string {
  const norm = (v: string | null) => {
    if (!v) return 'zzz';
    return v.trim().toLowerCase().padStart(12, '0');
  };
  return `${norm(e.carton)}|${norm(e.sous_farde)}|${norm(e.piece)}`;
}

export function sortPiecesByInventory<T extends PdfMetadata>(
  pieces: readonly T[],
  entries: readonly InventaireEntry[],
  preference: InventorySourcePreference = 'auto'
): T[] {
  const filteredEntries = entries.filter((e) => {
    if (preference === 'auto') return true;
    return e.source === preference;
  });
  // entryId → rang dans l'inventaire trié canoniquement.
  const sortedEntries = [...filteredEntries].sort((a, b) =>
    inventaireKey(a).localeCompare(inventaireKey(b), 'fr', { numeric: true })
  );
  const rankByEntryId = new Map<string, number>();
  sortedEntries.forEach((e, idx) => rankByEntryId.set(e.id, idx));

  // Séparer pièces avec un entry connu vs pièces orphelines.
  const linked: T[] = [];
  const orphans: T[] = [];
  for (const p of pieces) {
    if (p.inventaire_entry_id && rankByEntryId.has(p.inventaire_entry_id)) {
      linked.push(p);
    } else {
      orphans.push(p);
    }
  }

  linked.sort((a, b) => {
    const ra = rankByEntryId.get(a.inventaire_entry_id ?? '') ?? Infinity;
    const rb = rankByEntryId.get(b.inventaire_entry_id ?? '') ?? Infinity;
    if (ra !== rb) return ra - rb;
    return comparePieces(a, b);
  });
  orphans.sort(comparePieces);

  return [...linked, ...orphans];
}
